import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Plane, Plus, Upload, Pencil, ArrowRight, Archive, Trash2,
    CheckCircle2, FileText, X, Info, QrCode,
} from "lucide-react";

/**
 * VAULT Travel — Trips + Boarding Passes.
 *
 * P0 goal (spec §22): the customer can OPEN Travel → CLICK Add Boarding
 * Pass → UPLOAD / SCAN / ENTER → REVIEW → SAVE → REOPEN. Every step
 * uses real backend state (`/api/travel/boarding-passes`). No fake
 * wallet buttons (spec §11-12) — hidden entirely until certs land.
 */
const AIRPORT_HINT = "e.g. MAA, DEL, BOM";

function todayISO() { return new Date().toISOString().slice(0, 10); }

function BoardingPassCard({ bp, onOpen }) {
    return (
        <button
            onClick={() => onOpen(bp)}
            className="w-full text-left rounded-2xl border border-stone-200 bg-white hover:border-amber-400 hover:shadow-md transition-all p-4 group"
            data-testid={`travel-bp-card-${bp.id}`}
        >
            <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center">
                    <Plane size={13} className="text-stone-600" />
                </div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">
                    {bp.airline} · {bp.flight_number}
                </div>
                {bp.has_attachment && <FileText size={11} className="text-stone-400 ml-auto" title="File attached" />}
            </div>
            <div className="text-lg font-semibold text-stone-900 tabular-nums">
                {bp.origin} <span className="text-stone-400 mx-1.5">→</span> {bp.destination}
            </div>
            <div className="text-[12px] text-stone-500 mt-1">
                {bp.travel_date}
                {bp.departure_time ? ` · Departs ${bp.departure_time}` : ""}
                {bp.seat ? ` · Seat ${bp.seat}` : ""}
            </div>
        </button>
    );
}

function EmptyState({ onAdd }) {
    return (
        <div className="rounded-2xl border border-dashed border-stone-300 bg-stone-50/60 p-8 text-center" data-testid="travel-empty">
            <div className="w-12 h-12 rounded-full bg-white border border-stone-200 flex items-center justify-center mx-auto mb-3">
                <Plane size={17} className="text-stone-500" />
            </div>
            <div className="text-base font-semibold text-stone-900 mb-1">YOUR TRAVEL, ORGANISED.</div>
            <p className="text-[13px] text-stone-500 max-w-md mx-auto mb-4">
                Have a booking already? Enter your PNR and let VAULT bring in the flight details.
            </p>
            <button
                onClick={onAdd}
                data-testid="travel-empty-add-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-400 hover:bg-amber-300 text-stone-900 text-[13px] font-semibold"
            >
                <Plus size={13} /> Fetch with PNR
            </button>
            <div className="text-[11.5px] text-stone-500 mt-3">
                Or upload a boarding pass · scan · enter manually
            </div>
        </div>
    );
}

// ─── Add / Edit modal ─────────────────────────────────────────────

async function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => { const s = r.result; const idx = String(s).indexOf(","); resolve(String(s).slice(idx + 1)); };
        r.onerror = reject;
        r.readAsDataURL(file);
    });
}

const EMPTY_FORM = {
    passenger_name: "", airline: "", flight_number: "", origin: "",
    destination: "", travel_date: todayISO(), departure_time: "",
    boarding_time: "", terminal: "", gate: "", seat: "",
    booking_reference: "", boarding_group: "", notes: "",
};

function AddBoardingPassModal({ open, onClose, onSaved, editing }) {
    // step order: pnr (primary) | pnr_review | choose (fallback) | upload | manual | paste | review
    const [step, setStep] = useState(editing ? "manual" : "pnr");
    const [form, setForm] = useState(EMPTY_FORM);
    const [attach, setAttach] = useState(null);   // { name, type, b64, sizeKb }
    const [busy, setBusy] = useState(false);
    const [pasteText, setPasteText] = useState("");
    const [pasteNote, setPasteNote] = useState("");
    // ── PNR fetch state ──
    const [pnrInput, setPnrInput] = useState("");
    const [pnrSurname, setPnrSurname] = useState("");
    const [pnrRequireSurname, setPnrRequireSurname] = useState(false);
    const [pnrError, setPnrError] = useState("");
    const [pnrBooking, setPnrBooking] = useState(null);        // successful fetch payload
    const [pnrBoardingReady, setPnrBoardingReady] = useState(false);
    const [pnrDuplicate, setPnrDuplicate] = useState(null);
    const fileInput = useRef(null);

    useEffect(() => {
        if (!open) return;
        if (editing) {
            setStep("manual");
            setForm({ ...EMPTY_FORM, ...editing });
            setAttach(null);
        } else {
            setStep("pnr"); setForm(EMPTY_FORM); setAttach(null);
            setPasteText(""); setPasteNote("");
            setPnrInput(""); setPnrSurname(""); setPnrRequireSurname(false);
            setPnrError(""); setPnrBooking(null); setPnrBoardingReady(false); setPnrDuplicate(null);
        }
    }, [open, editing]);

    if (!open) return null;

    const canSave = form.airline && form.flight_number && form.origin && form.destination && form.travel_date;

    const pickFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const ext = (f.name.split(".").pop() || "").toLowerCase();
        if (!["pdf", "png", "jpg", "jpeg"].includes(ext)) {
            toast.error("Unsupported file type. Please upload a PDF, PNG or JPG.");
            return;
        }
        if (f.size > 4 * 1024 * 1024) {
            toast.error("File is too large (max 4 MB).");
            return;
        }
        const b64 = await fileToBase64(f);
        setAttach({ name: f.name, type: ext, b64, sizeKb: Math.round(f.size / 1024) });
        setStep("manual");
    };

    const save = async () => {
        setBusy(true);
        try {
            const body = { ...form };
            if (attach) {
                body.attachment_name = attach.name;
                body.attachment_type = attach.type;
                body.attachment_b64  = attach.b64;
            }
            let res;
            if (editing) {
                res = await api.patch(`/travel/boarding-passes/${editing.id}`, body);
            } else {
                try {
                    res = await api.post("/travel/boarding-passes", body);
                } catch (err) {
                    if (err?.response?.status === 409) {
                        const proceed = window.confirm(
                            "A boarding pass for this flight and date already exists. Save anyway?");
                        if (!proceed) { setBusy(false); return; }
                        res = await api.post("/travel/boarding-passes?allow_duplicate=true", body);
                    } else { throw err; }
                }
            }
            toast.success(editing ? "Boarding pass updated" : "Boarding pass saved to VAULT");
            onSaved(res.data.boarding_pass);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not save boarding pass";
            toast.error(typeof msg === "string" ? msg : (msg.message || "Save failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            data-testid="travel-add-modal"
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="bg-white rounded-2xl max-w-xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
                <header className="px-5 py-4 border-b border-stone-100 flex items-center gap-2">
                    <Plane size={16} className="text-amber-600" />
                    <div className="text-[15px] font-semibold text-stone-900">
                        {editing ? "Edit boarding pass" : "Add your flight"}
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="ml-auto rounded-full p-1 hover:bg-stone-100"
                        data-testid="travel-modal-close"
                    ><X size={16} /></button>
                </header>

                {step === "pnr" && (
                    <div className="p-5 space-y-3" data-testid="travel-pnr-step">
                        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
                            <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700 mb-1">FETCH WITH PNR</div>
                            <p className="text-[13px] text-stone-700">
                                Enter your booking reference and VAULT will try to retrieve your flight details.
                            </p>
                        </div>
                        <Field
                            label="Booking Reference / PNR *"
                            value={pnrInput}
                            onChange={(v) => { setPnrInput(v.toUpperCase()); setPnrError(""); }}
                            placeholder="ABC123"
                            testId="travel-pnr-input"
                        />
                        {pnrRequireSurname && (
                            <Field
                                label="Passenger Last Name *"
                                value={pnrSurname}
                                onChange={setPnrSurname}
                                placeholder=""
                                testId="travel-pnr-surname"
                            />
                        )}
                        {pnrError && (
                            <div className="text-[12.5px] text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2" data-testid="travel-pnr-error">
                                {pnrError}
                            </div>
                        )}
                        <div className="flex items-center justify-between gap-2 pt-1">
                            <button
                                onClick={() => setStep("choose")}
                                data-testid="travel-pnr-fallback-link"
                                className="text-[12.5px] text-stone-600 hover:text-stone-900"
                            >
                                Upload · scan · enter manually →
                            </button>
                            <button
                                onClick={async () => {
                                    setPnrError("");
                                    if (!/^[A-Z0-9]{6}$/.test(pnrInput)) {
                                        setPnrError("Please enter a 6-character booking reference.");
                                        return;
                                    }
                                    setBusy(true);
                                    try {
                                        const r = await api.post("/travel/pnr/lookup", {
                                            pnr: pnrInput,
                                            surname: pnrSurname || undefined,
                                        });
                                        const d = r.data || {};
                                        if (d.status === "found") {
                                            setPnrBooking(d.booking);
                                            setPnrBoardingReady(!!d.boarding_pass_ready);
                                            setPnrDuplicate(d.duplicate || null);
                                            setStep("pnr_review");
                                        } else if (d.status === "unconfigured" || d.status === "provider_error") {
                                            setPnrError(d.message || "We couldn't retrieve this booking automatically.");
                                            setTimeout(() => setStep("choose"), 900);
                                        } else {
                                            setPnrError(d.message || "Booking not found. Please check the reference and passenger name.");
                                            if (!pnrRequireSurname) setPnrRequireSurname(true);
                                        }
                                    } catch (err) {
                                        if (err?.response?.status === 429) {
                                            setPnrError("Too many lookups. Please wait a minute and try again.");
                                        } else if (err?.response?.status === 422) {
                                            setPnrError("Please enter a 6-character booking reference.");
                                        } else {
                                            setPnrError("We couldn't retrieve this booking. Please try again or use another method.");
                                        }
                                    } finally {
                                        setBusy(false);
                                    }
                                }}
                                disabled={busy || !pnrInput}
                                data-testid="travel-pnr-fetch-btn"
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-400 hover:bg-amber-300 disabled:bg-stone-200 disabled:text-stone-500 text-stone-900 text-[13px] font-semibold"
                            >
                                {busy ? "Fetching…" : "Fetch Booking →"}
                            </button>
                        </div>
                    </div>
                )}

                {step === "pnr_review" && pnrBooking && (
                    <div className="p-5 space-y-3" data-testid="travel-pnr-review">
                        <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-700">
                            {pnrBoardingReady ? "BOARDING PASS READY" : "FLIGHT ADDED TO VAULT"}
                        </div>
                        {!pnrBoardingReady && (
                            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12.5px] text-amber-900">
                                Boarding pass not available yet. VAULT will show itinerary and trip details.
                            </div>
                        )}
                        {pnrDuplicate && (
                            <div className="rounded-lg bg-stone-50 border border-stone-200 px-3 py-2 text-[12.5px] text-stone-700" data-testid="travel-pnr-duplicate">
                                This trip is already in your VAULT.
                            </div>
                        )}
                        <div className="text-[12.5px] text-stone-500">
                            Booking · <span className="font-mono text-stone-900">{pnrBooking.booking_reference}</span>
                            {pnrBooking.passenger_name ? ` · ${pnrBooking.passenger_name}` : ""}
                        </div>
                        <div className="rounded-xl border border-stone-200 divide-y divide-stone-100">
                            {(pnrBooking.segments || []).map((seg, i) => (
                                <div key={i} className="p-3" data-testid={`travel-pnr-seg-${i}`}>
                                    <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">
                                        {seg.airline || "—"} {seg.flight_number || ""}
                                    </div>
                                    <div className="text-lg font-semibold text-stone-900 tabular-nums">
                                        {seg.origin || "—"} <span className="text-stone-400 mx-1.5">→</span> {seg.destination || "—"}
                                    </div>
                                    <div className="text-[12px] text-stone-500 mt-1">
                                        {seg.travel_date || "Date not available yet"}
                                        {seg.departure_time ? ` · Departs ${seg.departure_time}` : ""}
                                        {seg.arrival_time ? ` · Arrives ${seg.arrival_time}` : ""}
                                    </div>
                                    <div className="text-[11px] text-stone-400 mt-1">
                                        Seat: {seg.seat || "Not available yet"} · Terminal: {seg.terminal || "Not available yet"} · Gate: {seg.gate || "Not available yet"}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center justify-end gap-2">
                            <button onClick={() => setStep("pnr")} className="text-[12.5px] text-stone-600 hover:text-stone-900 px-3 py-2">Back</button>
                            <button
                                onClick={async () => {
                                    setBusy(true);
                                    try {
                                        const url = pnrDuplicate
                                            ? "/travel/pnr/save-booking?allow_duplicate=true"
                                            : "/travel/pnr/save-booking";
                                        const r = await api.post(url, { booking: pnrBooking });
                                        toast.success(r.data.message || "Flight saved to VAULT");
                                        onSaved(r.data.saved?.[0]);
                                    } catch (err) {
                                        if (err?.response?.status === 409) {
                                            const proceed = window.confirm("This trip is already in your VAULT. Save another copy?");
                                            if (proceed) {
                                                const r2 = await api.post("/travel/pnr/save-booking?allow_duplicate=true", { booking: pnrBooking });
                                                toast.success("Trip saved");
                                                onSaved(r2.data.saved?.[0]);
                                            }
                                        } else {
                                            toast.error(err?.response?.data?.detail || "Could not save");
                                        }
                                    } finally {
                                        setBusy(false);
                                    }
                                }}
                                disabled={busy}
                                data-testid="travel-pnr-save-btn"
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-400 hover:bg-amber-300 text-stone-900 text-[13px] font-semibold"
                            >
                                {busy ? "Saving…" : "Save to VAULT"}
                            </button>
                        </div>
                    </div>
                )}

                {step === "choose" && (
                    <div className="p-5 space-y-2">
                        <p className="text-[13px] text-stone-500 mb-2">
                            Choose how you&rsquo;d like to add your boarding pass.
                        </p>
                        <button
                            onClick={() => fileInput.current?.click()}
                            data-testid="travel-method-upload"
                            className="w-full text-left rounded-xl border border-stone-200 hover:border-amber-400 hover:bg-amber-50/30 p-4 flex items-start gap-3"
                        >
                            <Upload size={16} className="text-amber-600 mt-0.5" />
                            <div>
                                <div className="text-[13.5px] font-semibold text-stone-900">Upload PDF or image</div>
                                <div className="text-[12px] text-stone-500">Attach the boarding pass, then confirm the flight details.</div>
                            </div>
                        </button>
                        <label className="block cursor-pointer md:hidden" data-testid="travel-method-scan-wrap">
                            <div className="w-full text-left rounded-xl border border-stone-200 hover:border-amber-400 hover:bg-amber-50/30 p-4 flex items-start gap-3">
                                <Upload size={16} className="text-amber-600 mt-0.5" />
                                <div>
                                    <div className="text-[13.5px] font-semibold text-stone-900">Take a photo</div>
                                    <div className="text-[12px] text-stone-500">Use your phone camera to capture the boarding pass.</div>
                                </div>
                            </div>
                            <input type="file" accept="image/*" capture="environment" className="hidden"
                                   onChange={pickFile} data-testid="travel-method-scan" />
                        </label>
                        <button
                            onClick={() => setStep("manual")}
                            data-testid="travel-method-manual"
                            className="w-full text-left rounded-xl border border-stone-200 hover:border-amber-400 hover:bg-amber-50/30 p-4 flex items-start gap-3"
                        >
                            <Pencil size={16} className="text-amber-600 mt-0.5" />
                            <div>
                                <div className="text-[13.5px] font-semibold text-stone-900">Add manually</div>
                                <div className="text-[12px] text-stone-500">Type the airline, flight and travel details yourself.</div>
                            </div>
                        </button>
                        <button
                            onClick={() => setStep("paste")}
                            data-testid="travel-method-paste"
                            className="w-full text-left rounded-xl border border-stone-200 hover:border-amber-400 hover:bg-amber-50/30 p-4 flex items-start gap-3"
                        >
                            <FileText size={16} className="text-amber-600 mt-0.5" />
                            <div>
                                <div className="text-[13.5px] font-semibold text-stone-900">Paste airline email</div>
                                <div className="text-[12px] text-stone-500">Drop in your IndiGo / Air India / Vistara / SpiceJet confirmation — we&rsquo;ll pre-fill the fields for your review.</div>
                            </div>
                        </button>
                        <input ref={fileInput} type="file" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/*"
                               className="hidden" onChange={pickFile} data-testid="travel-file-input" />
                    </div>
                )}

                {step === "paste" && (
                    <div className="p-5 space-y-3">
                        <p className="text-[12.5px] text-stone-500">
                            Paste the confirmation email or SMS from your airline. We&rsquo;ll extract what we can — you review before saving.
                        </p>
                        <textarea
                            value={pasteText}
                            onChange={(e) => setPasteText(e.target.value)}
                            rows={8}
                            data-testid="travel-paste-textarea"
                            className="w-full rounded-lg border border-stone-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none px-3 py-2 text-[13px] font-mono"
                            placeholder="e.g. Your IndiGo booking is confirmed. PNR: ABC123. Flight 6E-345 from MAA to DEL on 20 Dec 2099. Departs 08:30. Seat 14A."
                        />
                        {pasteNote && <div className="text-[12px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">{pasteNote}</div>}
                        <div className="flex items-center justify-end gap-2">
                            <button
                                onClick={() => setStep("choose")}
                                className="text-[12.5px] text-stone-600 hover:text-stone-900 px-3 py-2"
                            >Back</button>
                            <button
                                onClick={async () => {
                                    if (!pasteText.trim()) { toast.error("Paste the email text first."); return; }
                                    setBusy(true);
                                    try {
                                        const r = await api.post("/travel/boarding-passes/from-email", { text: pasteText });
                                        const f = r.data.fields || {};
                                        setForm({
                                            ...EMPTY_FORM,
                                            airline:           f.airline           || "",
                                            flight_number:     f.flight_number     || "",
                                            origin:            f.origin            || "",
                                            destination:       f.destination       || "",
                                            travel_date:       f.travel_date       || todayISO(),
                                            departure_time:    f.departure_time    || "",
                                            seat:              f.seat              || "",
                                            booking_reference: f.booking_reference || "",
                                        });
                                        setPasteNote(r.data.note || "");
                                        setStep("manual");
                                    } catch (err) {
                                        toast.error(err?.response?.data?.detail || "Could not parse — please add manually.");
                                    } finally {
                                        setBusy(false);
                                    }
                                }}
                                disabled={busy || !pasteText.trim()}
                                data-testid="travel-paste-extract-btn"
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-400 hover:bg-amber-300 disabled:bg-stone-200 disabled:text-stone-500 text-stone-900 text-[13px] font-semibold"
                            >
                                {busy ? "Reading…" : "Extract & review"}
                            </button>
                        </div>
                    </div>
                )}

                {step === "manual" && (
                    <div className="p-5 space-y-3">
                        {attach && (
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 flex items-center gap-2 text-[12px] text-emerald-800">
                                <FileText size={12} /> <span className="flex-1 truncate">{attach.name}</span>
                                <span className="text-emerald-600">{attach.sizeKb} KB</span>
                                <button onClick={() => setAttach(null)} className="text-emerald-700 hover:text-emerald-900" data-testid="travel-remove-attach">
                                    <X size={12} />
                                </button>
                            </div>
                        )}
                        <div className="rounded-lg bg-amber-50/60 border border-amber-100 px-3 py-2 flex items-start gap-2 text-[12px] text-amber-900">
                            <Info size={12} className="mt-0.5" />
                            <div>VAULT organises your boarding pass — we don&rsquo;t issue airline tickets. Please review each field before saving.</div>
                        </div>
                        <Field label="Airline *" value={form.airline} onChange={(v) => setForm({...form, airline: v})} placeholder="e.g. IndiGo" testId="travel-f-airline" />
                        <Field label="Flight number *" value={form.flight_number} onChange={(v) => setForm({...form, flight_number: v.toUpperCase()})} placeholder="e.g. 6E345" testId="travel-f-flight" />
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="From *" value={form.origin} onChange={(v) => setForm({...form, origin: v.toUpperCase()})} placeholder={AIRPORT_HINT} testId="travel-f-origin" />
                            <Field label="To *" value={form.destination} onChange={(v) => setForm({...form, destination: v.toUpperCase()})} placeholder={AIRPORT_HINT} testId="travel-f-dest" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Travel date *" type="date" value={form.travel_date} onChange={(v) => setForm({...form, travel_date: v})} testId="travel-f-date" />
                            <Field label="Departure time" type="time" value={form.departure_time} onChange={(v) => setForm({...form, departure_time: v})} testId="travel-f-dep" />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <Field label="Seat" value={form.seat} onChange={(v) => setForm({...form, seat: v})} placeholder="14A" testId="travel-f-seat" />
                            <Field label="Gate" value={form.gate} onChange={(v) => setForm({...form, gate: v})} testId="travel-f-gate" />
                            <Field label="Terminal" value={form.terminal} onChange={(v) => setForm({...form, terminal: v})} testId="travel-f-term" />
                        </div>
                        <Field label="Passenger name" value={form.passenger_name} onChange={(v) => setForm({...form, passenger_name: v})} testId="travel-f-name" />
                        <Field label="Booking reference / PNR" value={form.booking_reference} onChange={(v) => setForm({...form, booking_reference: v.toUpperCase()})} testId="travel-f-pnr" />
                        <div className="flex items-center justify-end gap-2 pt-2">
                            {!editing && (
                                <button
                                    onClick={() => setStep("choose")}
                                    className="text-[12.5px] text-stone-600 hover:text-stone-900 px-3 py-2"
                                >
                                    Back
                                </button>
                            )}
                            <button
                                onClick={save}
                                disabled={!canSave || busy}
                                data-testid="travel-save-btn"
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-400 hover:bg-amber-300 disabled:bg-stone-200 disabled:text-stone-500 text-stone-900 text-[13px] font-semibold"
                            >
                                {busy ? "Saving…" : (editing ? "Save changes" : "Save boarding pass")}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Field({ label, value, onChange, placeholder, type = "text", testId }) {
    return (
        <label className="block">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1">{label}</div>
            <input
                type={type}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testId}
                className="w-full rounded-lg border border-stone-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none px-3 py-2 text-[13.5px] text-stone-900"
            />
        </label>
    );
}

// ─── Detail drawer ────────────────────────────────────────────────

function DetailDrawer({ bp, onClose, onEdit, onArchive, onDelete }) {
    const navigate = useNavigate();
    const [attachment, setAttachment] = useState(null);
    useEffect(() => {
        if (!bp || !bp.has_attachment) return;
        api.get(`/travel/boarding-passes/${bp.id}/attachment`)
            .then((r) => setAttachment(r.data))
            .catch(() => setAttachment(null));
    }, [bp?.id]);

    if (!bp) return null;
    return (
        <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <aside className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl" data-testid={`travel-detail-${bp.id}`}>
                <header className="px-5 py-4 border-b border-stone-100 flex items-center gap-2">
                    <Plane size={16} className="text-amber-600" />
                    <div className="text-[14px] font-semibold text-stone-900 truncate">
                        {bp.airline} {bp.flight_number}
                    </div>
                    <button onClick={onClose} className="ml-auto rounded-full p-1 hover:bg-stone-100" aria-label="Close" data-testid="travel-detail-close">
                        <X size={16} />
                    </button>
                </header>
                <div className="p-5 space-y-4">
                    <div className="text-3xl font-semibold text-stone-900 tabular-nums">
                        {bp.origin} <span className="text-stone-400 mx-2">→</span> {bp.destination}
                    </div>
                    <div className="text-[13px] text-stone-500">
                        {bp.travel_date}
                        {bp.departure_time ? ` · Departs ${bp.departure_time}` : ""}
                        {bp.boarding_time ? ` · Boards ${bp.boarding_time}` : ""}
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-[12.5px]">
                        <Meta label="Seat"     value={bp.seat} />
                        <Meta label="Gate"     value={bp.gate} />
                        <Meta label="Terminal" value={bp.terminal} />
                    </div>
                    {(bp.passenger_name || bp.booking_reference) && (
                        <div className="grid grid-cols-2 gap-3 text-[12.5px]">
                            <Meta label="Passenger" value={bp.passenger_name} />
                            <Meta label="Booking"   value={bp.booking_reference} />
                        </div>
                    )}
                    {bp.has_attachment && attachment && (
                        <div className="rounded-lg border border-stone-200 overflow-hidden">
                            <div className="px-3 py-2 border-b border-stone-100 text-[11px] font-semibold uppercase tracking-wider text-stone-500">Attached pass</div>
                            {attachment.attachment_type === "pdf" ? (
                                <embed
                                    src={`data:application/pdf;base64,${attachment.attachment_b64}`}
                                    type="application/pdf"
                                    className="w-full"
                                    style={{ height: 420 }}
                                />
                            ) : (
                                <img
                                    src={`data:image/${attachment.attachment_type};base64,${attachment.attachment_b64}`}
                                    alt="Boarding pass"
                                    className="w-full"
                                />
                            )}
                        </div>
                    )}
                    <div className="rounded-lg bg-stone-50 border border-stone-200 px-3 py-2 flex items-start gap-2 text-[11.5px] text-stone-600">
                        <Info size={12} className="mt-0.5" />
                        <div>
                            Apple Wallet and Google Wallet buttons will appear here once the VAULT signing certificates are provisioned.
                            Until then, your boarding pass is safely organised in VAULT.
                        </div>
                    </div>
                    {bp.has_attachment && bp.status !== "archived" && (
                        <button
                            onClick={() => navigate(`/travel/airport-mode/${bp.id}`)}
                            data-testid="travel-detail-airport-mode"
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-amber-400 hover:bg-amber-300 text-stone-900 text-[14px] font-semibold shadow-sm"
                        >
                            <QrCode size={15} /> SHOW BOARDING PASS
                        </button>
                    )}
                    <div className="flex items-center gap-2 pt-2">
                        <button onClick={() => onEdit(bp)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-stone-300 hover:border-stone-500 text-[12.5px] font-semibold text-stone-800" data-testid="travel-detail-edit">
                            <Pencil size={12} /> Edit
                        </button>
                        <button onClick={() => onArchive(bp)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-stone-300 hover:border-stone-500 text-[12.5px] font-semibold text-stone-800" data-testid="travel-detail-archive">
                            <Archive size={12} /> Archive
                        </button>
                        <button onClick={() => onDelete(bp)} className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-rose-300 hover:border-rose-500 text-[12.5px] font-semibold text-rose-700" data-testid="travel-detail-delete">
                            <Trash2 size={12} /> Delete
                        </button>
                    </div>
                </div>
            </aside>
        </div>
    );
}

function Meta({ label, value }) {
    if (!value) return null;
    return (
        <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-1">{label}</div>
            <div className="text-[13.5px] text-stone-900 font-medium">{value}</div>
        </div>
    );
}

// ─── Page ─────────────────────────────────────────────────────────

export default function Travel() {
    const { section } = useParams();      // /organise/travel/:section?  (unused for now)
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [detail, setDetail] = useState(null);
    const [tab, setTab] = useState("upcoming");

    const refresh = async () => {
        try {
            const r = await api.get("/travel/boarding-passes");
            setData(r.data);
        } catch { setData({ upcoming: [], past: [], archived: [], counts: { upcoming: 0, past: 0, archived: 0 } }); }
    };
    useEffect(() => { refresh(); }, []);

    // Auto-open the Add modal when the URL is /organise/travel/add — matches
    // Quick Add and the dashboard empty-state CTA.
    useEffect(() => {
        if (section === "add") { setModalOpen(true); setEditing(null); }
    }, [section]);

    const rows = useMemo(() => data ? data[tab] || [] : [], [data, tab]);

    return (
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8" data-testid="travel-page">
            <header className="flex items-baseline justify-between mb-6">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Organise · Travel</div>
                    <h1 className="text-3xl font-semibold text-stone-900 mt-1">Boarding passes &amp; trips</h1>
                    <p className="text-[13px] text-stone-500 mt-1">Everything you need at the airport, in one place.</p>
                </div>
                <button
                    onClick={() => { setEditing(null); setModalOpen(true); }}
                    data-testid="travel-add-btn"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-400 hover:bg-amber-300 text-stone-900 text-[13px] font-semibold shadow-sm"
                >
                    <Plus size={14} /> Add Boarding Pass
                </button>
            </header>

            <div className="flex items-center gap-1 mb-4 border-b border-stone-200" data-testid="travel-tabs">
                {[
                    ["upcoming", "Upcoming"],
                    ["past",     "Past"],
                    ["archived", "Archived"],
                ].map(([key, label]) => (
                    <button
                        key={key}
                        onClick={() => setTab(key)}
                        data-testid={`travel-tab-${key}`}
                        className={`px-3 py-2 text-[13px] font-semibold border-b-2 -mb-px transition-colors ${
                            tab === key ? "border-amber-500 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-800"
                        }`}
                    >
                        {label}
                        <span className="ml-1.5 text-[11px] text-stone-400 tabular-nums">
                            {data?.counts?.[key] ?? 0}
                        </span>
                    </button>
                ))}
            </div>

            {data === null ? (
                <div className="text-[13px] text-stone-500 py-6">Loading your boarding passes…</div>
            ) : rows.length === 0 ? (
                <EmptyState onAdd={() => { setEditing(null); setModalOpen(true); }} />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="travel-grid">
                    {rows.map((bp) => (
                        <BoardingPassCard key={bp.id} bp={bp} onOpen={setDetail} />
                    ))}
                </div>
            )}

            <AddBoardingPassModal
                open={modalOpen}
                editing={editing}
                onClose={() => { setModalOpen(false); setEditing(null); if (section === "add") navigate("/organise/travel", { replace: true }); }}
                onSaved={(bp) => {
                    setModalOpen(false); setEditing(null);
                    if (section === "add") navigate("/organise/travel", { replace: true });
                    refresh().then(() => { if (bp) setDetail({ ...bp, has_attachment: bp.has_attachment ?? !!bp.attachment_name }); });
                }}
            />
            <DetailDrawer
                bp={detail}
                onClose={() => setDetail(null)}
                onEdit={(bp) => { setDetail(null); setEditing(bp); setModalOpen(true); }}
                onArchive={async (bp) => {
                    try {
                        await api.post(`/travel/boarding-passes/${bp.id}/archive`);
                        toast.success("Boarding pass archived");
                        setDetail(null); refresh();
                    } catch { toast.error("Could not archive"); }
                }}
                onDelete={async (bp) => {
                    if (!window.confirm("Delete this boarding pass? This can't be undone.")) return;
                    try {
                        await api.delete(`/travel/boarding-passes/${bp.id}`);
                        toast.success("Boarding pass deleted");
                        setDetail(null); refresh();
                    } catch { toast.error("Could not delete"); }
                }}
            />

            <div className="mt-8 text-[12px] text-stone-500">
                Trips landing page and shared travel calendar are coming soon.&nbsp;
                <Link to="/trips" className="text-amber-700 hover:underline inline-flex items-center gap-1">
                    Existing Trips <ArrowRight size={11} />
                </Link>
            </div>
        </div>
    );
}
